import { ICONO, type Icono } from '../ui/iconos.js';

/**
 * La caja de ícono (icon-box de la referencia): un ícono de la librería sobre un
 * cuadrado redondeado con el tinte de su familia.
 *
 * El color dice **de qué se trata**, nunca cómo está el paciente: navy para lo
 * del programa, teal para lo hecho, naranja para lo que el paciente reporta,
 * violeta para el cuidador y PIA, ámbar para lo operativo (el silencio, un
 * contacto perdido). Por eso no hay variante roja, amarilla ni verde: esos son
 * los colores del semáforo y sólo los usa el semáforo.
 *
 * Las variantes `-fuerte` y `marca` son las invertidas: la caja de una opción
 * elegida y la del KPI sobre la superficie navy.
 *
 * Todos los pares fondo/tinta pasan AA para el ícono (3:1, WCAG 1.4.11): el más
 * justo es teal-dark sobre teal-100, 4,9:1.
 */

export type TonoDeCaja =
  | 'teal'
  | 'navy'
  | 'naranja'
  | 'violeta'
  | 'ambar'
  | 'gris'
  | 'medico'
  | 'marca'
  | 'navy-fuerte'
  | 'naranja-fuerte';

const TONO: Readonly<Record<TonoDeCaja, string>> = Object.freeze({
  teal: 'bg-accent-soft text-accent-strong',
  navy: 'bg-brand-soft text-link',
  naranja: 'bg-warning-soft text-warning-strong',
  violeta: 'bg-role-cuidador-bg text-role-cuidador',
  ambar: 'bg-priority-p1-bg text-role-admin',
  gris: 'bg-muted text-subtle',
  medico: 'bg-role-medico-bg text-role-medico',
  // Las invertidas: sobre la superficie de marca, o la caja de lo elegido.
  marca: 'bg-accent text-heading',
  'navy-fuerte': 'bg-brand text-primary-foreground',
  'naranja-fuerte': 'bg-warning-strong text-primary-foreground',
});

const TAMANO = Object.freeze({
  chica: { caja: 'size-11', icono: ICONO.normal },
  normal: { caja: 'size-13', icono: ICONO.grande },
  grande: { caja: 'size-14', icono: ICONO.grande },
});

export function CajaDeIcono({
  icono: Icono,
  tono = 'teal',
  tamano = 'normal',
  className = '',
}: {
  icono: Icono;
  tono?: TonoDeCaja;
  tamano?: keyof typeof TAMANO;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-lg ${TAMANO[tamano].caja} ${TONO[tono]} ${className}`}
    >
      <Icono className={TAMANO[tamano].icono} />
    </span>
  );
}
